import { CSSProperties } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';

export type LozengeAppearance = 'blue' | 'neutral' | 'saffron' | 'lime' | 'purple' | 'danger';

export type LozengeProps = {
	appearance?: LozengeAppearance;
	text: string | React.ReactNode;
};

const appearanceColors = {
	blue: {
		backgroundColor: wacToken('color.extended.blue100'),
		color: wacToken('color.extended.blue800'),
	},
	neutral: {
		backgroundColor: token('color.background.neutral'),
		color: token('color.text.subtle'),
	},
	saffron: {
		backgroundColor: wacToken('color.extended.saffron100'),
		color: wacToken('color.extended.saffron800'),
	},
	lime: {
		backgroundColor: wacToken('color.extended.lime100'),
		color: wacToken('color.extended.lime800'),
	},
	purple: {
		backgroundColor: wacToken('color.extended.purple100'),
		color: wacToken('color.extended.purple800'),
	},
	danger: {
		backgroundColor: token('color.background.danger', '#FFEBE6'),
		color: wacToken('color.secondary.red'),
	},
};

export const Lozenge = ({ appearance = 'neutral', text }: LozengeProps) => {
	const isWacRefresh = useWacRefresh();

	if (!text) {
		return null;
	}

	const colors = appearanceColors[appearance] ?? appearanceColors.neutral;

	return (
		<div
			style={
				{
					'--lozenge-bg': colors.backgroundColor,
					'--lozenge-color': colors.color,
				} as CSSProperties
			}
			css={[lozengeStyles, isWacRefresh ? refreshLozengeStyles : nonRefreshLozengeStyles]}
		>
			{text}
		</div>
	);
};

const lozengeStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: '10px',
	paddingInline: '8px',
	textAlign: 'center',
	fontFamily: wacToken('font.charlie.text'),
	fontWeight: 500,
	letterSpacing: '0.3px',
	textTransform: 'uppercase',
	maxWidth: '200px',
	boxSizing: 'border-box',
	flexShrink: 0,
	width: 'fit-content',
	backgroundColor: 'var(--lozenge-bg)',
	color: 'var(--lozenge-color)',
});

const nonRefreshLozengeStyles = css({
	fontSize: '11px',
	lineHeight: '16px',
	borderRadius: '20px',
});

const refreshLozengeStyles = css({
	borderRadius: '3px',
	fontSize: '12px',
	lineHeight: '16px',
	[media.above.md]: {
		fontSize: '14px',
		lineHeight: '18px',
	},
	[media.above.lg]: {
		fontSize: '16px',
		lineHeight: '18px',
	},
});
